import React, {useCallback, useState} from 'react'

import Image from 'next/image'
import styled from 'styled-components'
import {palette} from 'styled-tools'
import {media} from 'styled-bootstrap-grid'
import {RiSubtractLine, RiAddLine} from '@remixicon/react'
import classNames from 'classnames'
import {useMedia} from 'react-use'
import useSWR from 'swr'

import {LogoDesktopDark} from '@festi/common/images'
import {MainIconButton, NewActionButton} from '@festi/common/components/buttons'
import {formatPercentage, formatPrice} from '@festi/utils/numbers'
import {P} from '@festi/common/components/typography'
import {styledTheme} from '@festi/common/themes'
import {Modal} from '@festi/common/components/common'
import {useAuth} from '@festi/common/contexts'

import PagoPaymentOptionsTabs from '../PagoPaymentOptionsTabs'
import {pagoInstallmentsOptions, pagoLoanCalculations} from '../api/fetch'
import {InstallmentsText} from './InstallmentsText'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 24px;
  background-color: ${palette('backgroundGrey')};
  border-radius: 16px;

  ${media.md`
    padding: 28px;
  `}
`

const Header = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;

  ${media.md`
    gap: 16px;
  `}
`

const HeaderInnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-grow: 1;
`

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  ${media.md`
    flex-direction: row;
  `}
`

const CalculationDetailsText = styled.p`
  color: ${palette('ui60Solid')};
  line-height: 18px;

  &.blurText {
    color: transparent;
    text-shadow: 0 0 6px ${palette('blue')};
  }
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  zoom: 0;
`

const DesktopLogo = styled(Image)`
  width: auto;
  height: 28px;

  ${media.md`
    height: 30px;
  `}
`

const DelayOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;

  &.blurText {
    color: transparent;
    text-shadow: 0 0 6px ${palette('blue')};
  }
`

const StyledP = styled(P)`
  width: fit-content;
`

const ModalContentWrapper = styled.div`
  width: 100%;

  ${media.sm`
    width: 592px;
  `}
`

interface PagoCalculatorProps {
  price: number
}

export default function PagoCalculator({
  price,
}: PagoCalculatorProps): JSX.Element {
  const {userLoading, selectedBusiness} = useAuth()
  const [detailsModalOpen, setDetailsModalOpen] = useState<boolean>(false)
  const [loanInstallments, setLoanInstallments] = useState<number>(2) // Default to 2 installments

  const {data: installmentsOptions, error} = useSWR(
    price ? [price, 'pago-installments-options'] : null,
    ([price]) => pagoInstallmentsOptions(price),
    {
      revalidateOnFocus: false,
      errorRetryCount: 3,
      keepPreviousData: !userLoading && !selectedBusiness,
    },
  )

  const loanOptionNotFound = error && error === 404

  const {data: pagoLoanCalculationData, isLoading} = useSWR(
    installmentsOptions && !loanOptionNotFound
      ? [price, loanInstallments, 'pago-loan-calculator']
      : null,
    ([price, loanInstallments]) =>
      pagoLoanCalculations(price, loanInstallments),
    {
      revalidateOnFocus: false,
      errorRetryCount: 3,
      keepPreviousData: true,
    },
  )

  const isLoadingLoanCalculation = isLoading || !pagoLoanCalculationData
  const minInstallments = installmentsOptions?.minimumInstallments
  const maxInstallments = installmentsOptions?.maximumInstallments

  const isMobile = useMedia(
    `(max-width: ${styledTheme.screenSize.sizeMD})`,
    false,
  )

  const handleMinusClick = useCallback(() => {
    setLoanInstallments(pagoLoanCalculationData?.installments - 1)
  }, [pagoLoanCalculationData?.installments, setLoanInstallments])

  const handlePlusClick = useCallback(() => {
    setLoanInstallments(pagoLoanCalculationData?.installments + 1)
  }, [pagoLoanCalculationData?.installments, setLoanInstallments])

  if (loanOptionNotFound || !installmentsOptions) return null

  return (
    <>
      <Root>
        <Header>
          <ButtonGroup>
            <MainIconButton
              onClick={handleMinusClick}
              icon={RiSubtractLine}
              disabled={
                pagoLoanCalculationData?.installments <= minInstallments ||
                isLoadingLoanCalculation
              }
              aria-label="Minus"
              buttonVariant="ghost"
              size={isMobile ? 'xsmall' : 'small'}
            />
            <MainIconButton
              onClick={handlePlusClick}
              icon={RiAddLine}
              disabled={
                pagoLoanCalculationData?.installments >= maxInstallments ||
                isLoadingLoanCalculation
              }
              aria-label="Plus"
              buttonVariant="ghost"
              size={isMobile ? 'xsmall' : 'small'}
            />
          </ButtonGroup>

          <HeaderInnerWrapper>
            <InstallmentsText
              installments={pagoLoanCalculationData?.installments || 12}
              averageMonthlyPayment={formatPrice(
                pagoLoanCalculationData?.averageMonthlyPayment || 0,
              )}
              isLoading={isLoadingLoanCalculation}
            />

            <IconContainer>
              <DesktopLogo src={LogoDesktopDark} alt="Elko" />
            </IconContainer>
          </HeaderInnerWrapper>
        </Header>

        <DelayOptionWrapper
          className={classNames({blurText: isLoadingLoanCalculation})}
        >
          <StyledP>eða frestaðu greiðslu í 14 daga. </StyledP>
          {/** Handle pago details here */}
          <NewActionButton
            buttonVariant="subtle"
            disabled={isLoadingLoanCalculation}
            onClick={() => setDetailsModalOpen(true)}
          >
            Skoða
          </NewActionButton>
        </DelayOptionWrapper>

        <CalculationDetailsText
          className={classNames({blurText: isLoadingLoanCalculation})}
        >
          {formatPercentage(pagoLoanCalculationData?.interestRate) || 0}%
          vextir,{' '}
          {formatPercentage(Number(pagoLoanCalculationData?.APR) * 100) || 0}%
          ÁHK, {formatPrice(pagoLoanCalculationData?.loanFeeAmount) || 0}{' '}
          lántökugjald,{' '}
          {formatPrice(
            pagoLoanCalculationData?.payments?.[0]?.installmentFeeAmount || 0,
          )}{' '}
          afborgunargjald,{' '}
          {formatPrice(
            pagoLoanCalculationData?.payments?.[0]?.paymentFeeAmount || 0,
          )}{' '}
          greiðslugjald. Heildargreiðsla{' '}
          {formatPrice(pagoLoanCalculationData?.totalAmount || 0)}
        </CalculationDetailsText>
      </Root>
      <Modal
        isOpen={detailsModalOpen}
        onRequestClose={() => setDetailsModalOpen(false)}
        title="Snjallgreiðslur"
        headerLogo
        bottomMobile
        fitContent
      >
        <ModalContentWrapper>
          <PagoPaymentOptionsTabs
            loanInstallments={loanInstallments}
            setLoanInstallments={setLoanInstallments}
            loanCalculationData={pagoLoanCalculationData}
            isLoading={isLoadingLoanCalculation}
            installmentsOptions={installmentsOptions}
          />
        </ModalContentWrapper>
      </Modal>
    </>
  )
}
